import type {
  GroupProDto,
  RecurringCadence,
  RecurringRuleDto,
  RecurringUnit,
  UserProDto,
} from '@splitfiesta/shared';
import {
  RECURRING_ERROR_KEYS,
  laterDate,
  nextOccurrence,
} from '@splitfiesta/shared';

import { EXAMPLE_GROUP_ID } from '../fixtures/exampleGroup';
import { groupPro, userPro } from '../pro/pro';

const nowIso = () => new Date().toISOString();

export const defaultCadence: RecurringCadence = { every: 1, unit: 'month' };

export interface RepeatAvailableArgs {
  proEnabled: boolean;
  user: { pro?: UserProDto } | null | undefined;
  group: { id: string; pro?: GroupProDto; currency: string };
  isOnline: boolean;
  entryCurrency: string;
  hasLineItems: boolean;
  mode: 'create' | 'edit';
}

/** Whether the expense form offers "Repeat"; mirrors what the server accepts. */
export function repeatAvailable(
  args: RepeatAvailableArgs,
  now: string = nowIso()
): boolean {
  if (
    args.mode !== 'create' ||
    !args.isOnline ||
    !args.proEnabled ||
    // The example group is client-side: a rule for it would reach the real API.
    args.group.id === EXAMPLE_GROUP_ID ||
    args.entryCurrency !== args.group.currency ||
    args.hasLineItems
  ) {
    return false;
  }
  return userPro(args.user, now).active || groupPro(args.group, now).active;
}

/**
 * The first date the rule will add after the one being saved now. A
 * backdated anchor never runs in the past, so the preview starts at today.
 */
export function repeatPreview({
  anchorDate,
  cadence,
  endDate,
  today,
}: {
  anchorDate: string;
  cadence: RecurringCadence;
  endDate?: string;
  today: string;
}): { nextDate: string; endsBefore: boolean } {
  const nextDate = nextOccurrence(
    anchorDate,
    cadence,
    laterDate(anchorDate, today)
  );
  return { nextDate, endsBefore: !!endDate && nextDate > endDate };
}

export type RecurringStatusKey =
  'active' | 'pausedUser' | 'pausedPro' | 'pausedMemberLeft' | 'ended';

export function recurringStatusKey(
  rule: Pick<RecurringRuleDto, 'status' | 'pausedReason'>
): RecurringStatusKey {
  if (rule.status === 'active') return 'active';
  if (rule.status === 'ended') return 'ended';
  if (rule.pausedReason === 'pro') return 'pausedPro';
  if (rule.pausedReason === 'member_left') return 'pausedMemberLeft';
  return 'pausedUser';
}

export interface RecurringActions {
  pause: boolean;
  resume: boolean;
  stop: boolean;
}

export interface RecurringViewer {
  id: string;
  role: 'owner' | 'admin' | 'member';
}

/**
 * Only the creator, an owner or an admin may manage a rule (the server
 * refuses anyone else). Only a pause the user made can be resumed by hand: a
 * Pro pause lifts when Pro returns, and a member who left cannot come back
 * into the split.
 */
export function recurringActions(
  rule: Pick<RecurringRuleDto, 'status' | 'pausedReason' | 'createdBy'>,
  viewer: RecurringViewer
): RecurringActions {
  if (
    rule.createdBy !== viewer.id &&
    viewer.role !== 'owner' &&
    viewer.role !== 'admin'
  ) {
    return { pause: false, resume: false, stop: false };
  }
  const key = recurringStatusKey(rule);
  return {
    pause: key === 'active',
    resume: key === 'pausedUser',
    stop: true,
  };
}

const RECURRING_ERROR_KEY_SET: ReadonlySet<string> = new Set(
  Object.values(RECURRING_ERROR_KEYS)
);

function translationKeyOf(error: unknown): unknown {
  return (error as { translationKey?: unknown } | null)?.translationKey;
}

export function isRecurringProRequiredError(error: unknown): boolean {
  return translationKeyOf(error) === RECURRING_ERROR_KEYS.proRequired;
}

export function recurringErrorKey(error: unknown): string | null {
  const key = translationKeyOf(error);
  return typeof key === 'string' && RECURRING_ERROR_KEY_SET.has(key)
    ? key
    : null;
}

/** Pass `count` to `t` on the bare key; `count` 1 picks the `_one` form. */
export function cadenceSentence(cadence: RecurringCadence): {
  key: `expenses:repeat.cadence.${RecurringUnit}`;
  count: number;
} {
  return {
    key: `expenses:repeat.cadence.${cadence.unit}`,
    count: cadence.every,
  };
}

export function unitLabel(
  unit: RecurringUnit,
  count: number
): { key: `expenses:repeat.unit.${RecurringUnit}`; count: number } {
  return { key: `expenses:repeat.unit.${unit}`, count };
}

/** The word before the count ("Every"); per unit because fr and de agree it with the unit. */
export function everyPrefix(
  unit: RecurringUnit,
  count: number
): { key: `expenses:repeat.everyPrefix.${RecurringUnit}`; count: number } {
  return { key: `expenses:repeat.everyPrefix.${unit}`, count };
}
